import { useState, useRef, useEffect } from "react";
import { Check, Shield } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

interface SliderCaptchaProps {
  onVerify: (verified: boolean) => void;
  onReset?: () => void;
  className?: string;
}

export const SliderCaptcha = ({ onVerify, onReset, className }: SliderCaptchaProps) => {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const [sliderPosition, setSliderPosition] = useState(0);
  const [isVerified, setIsVerified] = useState(false);
  const sliderRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const VERIFY_THRESHOLD = 0.85; // 85% della larghezza per migliore UX

  useEffect(() => {
    // Reset automatico dopo 30 secondi di inattività
    if (!isVerified && sliderPosition === 0) {
      const timeout = setTimeout(() => {
        handleReset();
      }, 30000);
      return () => clearTimeout(timeout);
    }
  }, [isVerified, sliderPosition]);

  const handleReset = () => {
    setSliderPosition(0);
    setIsVerified(false);
    onVerify(false);
    onReset?.();
  };

  const handleStart = (e: React.MouseEvent | React.TouchEvent) => {
    if (isVerified) return;
    e.preventDefault();
    setIsDragging(true);
  };

  const handleMove = (e: MouseEvent | TouchEvent) => {
    if (!isDragging || isVerified || !containerRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const relativeX = clientX - containerRect.left;
    
    // Calcolo corretto: considera la larghezza del bottone (48px)
    const maxMovement = containerRect.width - 48;
    const newPosition = Math.max(0, Math.min(1, relativeX / maxMovement));
    
    setSliderPosition(newPosition);

    // Verifica se raggiunto il threshold - snap automatico
    if (newPosition >= VERIFY_THRESHOLD) {
      setIsVerified(true);
      setSliderPosition(1);
      onVerify(true);
    }
  };

  const handleEnd = () => {
    if (isVerified) return;
    setIsDragging(false);

    // Se non verificato, reset
    if (sliderPosition < VERIFY_THRESHOLD) {
      setSliderPosition(0);
    }
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMove);
      window.addEventListener('mouseup', handleEnd);
      window.addEventListener('touchmove', handleMove);
      window.addEventListener('touchend', handleEnd);

      return () => {
        window.removeEventListener('mousemove', handleMove);
        window.removeEventListener('mouseup', handleEnd);
        window.removeEventListener('touchmove', handleMove);
        window.removeEventListener('touchend', handleEnd);
      };
    }
  }, [isDragging, sliderPosition]);

  return (
    <div className={cn("space-y-2", className)}>
      <label className="text-sm font-bold flex items-center gap-2">
        <Shield className="w-4 h-4" />
        {t("auth.captcha.title")}
      </label>
      
      <div
        ref={containerRef}
        className="relative h-12 bg-muted border-[3px] border-dark overflow-hidden select-none"
      >
        {/* Progress bar */}
        <div
          className={cn(
            "absolute inset-0 transition-all duration-200",
            isVerified ? "bg-primary/30" : isDragging ? "bg-primary/10" : "bg-transparent"
          )}
          style={{
            width: `${sliderPosition * 100}%`,
          }}
        />

        {/* Text */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className={cn(
            "font-bold text-sm transition-opacity",
            sliderPosition > 0.3 && "opacity-0"
          )}>
            {isVerified ? t("auth.captcha.verified") : t("auth.captcha.slideToVerify")}
          </span>
        </div>

        {/* Slider button */}
        <div
          ref={sliderRef}
          className={cn(
            "absolute top-0 left-0 h-full w-12 flex items-center justify-center border-r-[3px] border-dark transition-all",
            "shadow-[3px_3px_0px_0px] shadow-dark/50",
            isDragging ? "cursor-grabbing scale-105 shadow-[4px_4px_0px_0px] shadow-dark" : "cursor-grab",
            isVerified ? "bg-primary text-primary-foreground" : "bg-primary/90 hover:bg-primary"
          )}
          style={{
            transform: `translateX(${sliderPosition * ((containerRef.current?.offsetWidth || 0) - 48)}px)`,
            transition: isDragging ? 'none' : 'transform 0.3s ease-out, scale 0.2s ease-out',
          }}
          onMouseDown={handleStart}
          onTouchStart={handleStart}
        >
          {isVerified ? (
            <Check className="w-6 h-6 text-primary-foreground font-bold" />
          ) : (
            <div className="flex flex-col gap-0.5">
              <div className="w-5 h-0.5 bg-primary-foreground rounded" />
              <div className="w-5 h-0.5 bg-primary-foreground rounded" />
              <div className="w-5 h-0.5 bg-primary-foreground rounded" />
            </div>
          )}
        </div>
      </div>

      {isVerified && (
        <p className="text-xs text-primary font-bold flex items-center gap-1">
          <Check className="w-3 h-3" />
          {t("auth.captcha.verified")}
        </p>
      )}
    </div>
  );
};
